/**
 * Per-service question config for the multi-step Mockup form.
 * Each service has:
 *   - label: shown on the service picker
 *   - blurb: 1-line context under the label
 *   - vehicleField: if truthy, ask for vehicle year/make/model and populate lead.vehicle_type
 *   - askBudget / askTimeline: whether to render those default selects
 *   - questions: array of {key, label, type, options?, placeholder?, required?}
 *     type: 'text' | 'textarea' | 'select' | 'multi'
 *     Answers are consolidated into `project_details` on submit as labelled Q&A pairs.
 */

const TIMELINE = ["ASAP", "Within 2 weeks", "1–2 months", "Just exploring"];
const BUDGET_VEHICLE = ["Under $1,500", "$1,500 – $3,500", "$3,500 – $7,500", "$7,500+", "Not sure"];
const BUDGET_HOME = ["Under $1,500", "$1,500 – $3,500", "$3,500 – $7,500", "$7,500+", "Not sure"];
const BUDGET_SIGNS = ["Under $500", "$500 – $1,500", "$1,500 – $5,000", "$5,000+", "Not sure"];

export const SERVICES = [
  {
    id: "vehicle-wrap",
    label: "Vehicle Wrap",
    blurb: "One truck or a whole fleet — branded to get you noticed.",
    vehicleField: {
      key: "vehicle_type",
      label: "Vehicle year / make / model",
      placeholder: "e.g. 2020 Ford Transit 250 (or your main vehicle)",
    },
    askBudget: true,
    budgetOptions: BUDGET_VEHICLE,
    askTimeline: true,
    questions: [
      {
        key: "vehicle_count",
        label: "How many vehicles?",
        type: "select",
        options: ["Just one", "2–3 vehicles", "4–6 vehicles", "7–15 vehicles", "16+ vehicles"],
        required: true,
      },
      {
        key: "coverage",
        label: "How much of the vehicle should be wrapped?",
        type: "select",
        options: ["Full wrap (entire vehicle)", "Partial wrap (sides + rear)", "Logos & accents only", "Not sure — recommend for me"],
        required: true,
      },
      {
        key: "design_status",
        label: "Do you already have a logo / brand design?",
        type: "select",
        options: ["Yes — ready to use", "Have a logo but need design help", "No — need everything designed", "Not sure"],
        required: true,
      },
    ],
  },
  {
    id: "window-tint",
    label: "Window Tint",
    blurb: "Ceramic film for your car, home, or business.",
    vehicleField: {
      key: "vehicle_type",
      label: "Vehicle year / make / model",
      placeholder: "e.g. 2023 Toyota Tundra",
      showIf: { key: "tint_type", values: ["My vehicle"] },
    },
    askBudget: true,
    budgetOptions: BUDGET_HOME,
    askTimeline: true,
    questions: [
      {
        key: "tint_type",
        label: "What are we tinting?",
        type: "select",
        options: ["My vehicle", "My home", "My business / commercial space"],
        required: true,
      },
      {
        key: "windows",
        label: "Which windows?",
        type: "select",
        options: ["All windows around", "Front two only", "Everything except front", "Not sure — need advice"],
        required: true,
        showIf: { key: "tint_type", values: ["My vehicle"] },
      },
      {
        key: "shade",
        label: "Shade preference",
        type: "select",
        options: ["Light legal (~35% VLT)", "Medium legal (~25–20% VLT)", "Dark legal (rear + factory-legal front)", "Not sure — walk me through it"],
        required: true,
        showIf: { key: "tint_type", values: ["My vehicle"] },
      },
      {
        key: "home_size",
        label: "Approximate home size",
        type: "select",
        options: ["Under 2,000 sq ft", "2,000 – 3,500 sq ft", "3,500 – 5,000 sq ft", "5,000+ sq ft"],
        required: true,
        showIf: { key: "tint_type", values: ["My home"] },
      },
      {
        key: "window_count",
        label: "Roughly how many windows need film?",
        type: "select",
        options: ["Just a few (1–5)", "One area / room (6–12)", "Whole home (13–30)", "Large home (30+)", "Not sure"],
        required: true,
        showIf: { key: "tint_type", values: ["My home"] },
      },
      {
        key: "goal",
        label: "What's your #1 priority?",
        type: "select",
        options: ["Lower cooling bills", "Block UV / stop furniture fading", "More privacy", "Reduce glare", "All of the above"],
        required: true,
        showIf: { key: "tint_type", values: ["My home"] },
      },
      {
        key: "building_type",
        label: "Type of space",
        type: "select",
        options: ["Office", "Retail storefront", "Medical / dental", "Restaurant", "Warehouse / industrial", "Other"],
        required: true,
        showIf: { key: "tint_type", values: ["My business / commercial space"] },
      },
      {
        key: "glass_size",
        label: "Approximate glass to cover",
        type: "select",
        options: ["A few windows / one room", "One floor / large storefront", "Whole building (multi-floor)", "Not sure — need a site visit"],
        required: true,
        showIf: { key: "tint_type", values: ["My business / commercial space"] },
      },
      {
        key: "goal_commercial",
        label: "What's the primary goal?",
        type: "select",
        options: ["Cut energy / cooling costs", "Privacy for staff or customers", "Frosted branding / storefront look", "Safety / security film", "Reduce glare on monitors"],
        required: true,
        showIf: { key: "tint_type", values: ["My business / commercial space"] },
      },
    ],
  },
  {
    id: "signs-and-banners",
    label: "Signs & Banners",
    blurb: "Storefront signs, banners, real estate, job-site graphics.",
    askBudget: true,
    budgetOptions: BUDGET_SIGNS,
    askTimeline: true,
    questions: [
      {
        key: "sign_type",
        label: "What kind of sign do you need?",
        type: "select",
        options: ["Storefront sign", "Trade-show / event banner", "Yard sign(s)", "Real estate sign", "Job-site / construction sign", "Interior / wall graphics", "Other"],
        required: true,
      },
      {
        key: "design_status",
        label: "Do you have a design ready?",
        type: "select",
        options: ["Yes — print-ready", "Have logo but need design", "No — need it designed", "Not sure"],
        required: true,
      },
    ],
  },
  {
    id: "not-sure",
    label: "Not sure yet — help me figure it out",
    blurb: "Tell us what you're trying to solve. Dave will guide you.",
    askTimeline: true,
    questions: [
      {
        key: "goal",
        label: "What are you trying to achieve?",
        type: "textarea",
        placeholder: "e.g. \"I want my truck to look more professional\", \"I need to lower my office AC bills\", \"I need signage for a grand opening\"…",
        required: true,
      },
    ],
  },
];

export function getServiceConfig(id) {
  return SERVICES.find((s) => s.id === id);
}

/**
 * Conditional display — a question (or vehicleField) with `showIf: { key, values }`
 * only renders/validates/submits when answers[key] is one of values.
 */
export function isQuestionVisible(item, answers) {
  if (!item.showIf) return true;
  return item.showIf.values.includes(answers[item.showIf.key]);
}

/**
 * Convert the collected service-specific answers into a structured multi-line string
 * that's saved as `project_details` on the lead. Formats cleanly in the admin email.
 */
export function buildProjectDetails(config, answers, freeText) {
  const lines = [];
  (config.questions || []).filter((q) => isQuestionVisible(q, answers)).forEach((q) => {
    const a = answers[q.key];
    if (a == null || a === "" || (Array.isArray(a) && a.length === 0)) return;
    const val = Array.isArray(a) ? a.join(", ") : a;
    lines.push(`${q.label}\n  → ${val}`);
  });
  if (freeText && freeText.trim()) {
    lines.push(`Anything else Dave should know?\n  → ${freeText.trim()}`);
  }
  return lines.join("\n\n") || "(no additional details provided)";
}
